import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import SetupProgress from "@/components/SetupProgress";
import Onboarding from "@/components/Onboarding";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import PaymentSuccessBanner from "@/components/PaymentSuccessBanner";
import LegacyHealth from "@/components/LegacyHealth";
import PoLBanner from "@/components/PoLBanner";
import {
  Lock,
  MessageSquareHeart,
  Users,
  HeartPulse,
  Bomb,
  BookHeart,
  ArrowUpRight,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

const PLAN_LABEL = { free: "Gratuito", personal: "Personal", family: "Familiar", premium: "Premium" };

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [checking, setChecking] = useState(false);
  // Bumped after a successful checkin so children that fetch their own
  // data (LegacyHealth) re-mount and re-fetch the new PoL signals.
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    const r = await api.get("/dashboard/summary");
    setSummary(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCheckin = async () => {
    setChecking(true);
    try {
      await api.post("/proof-of-life/checkin");
      await load();
      setRefreshKey((k) => k + 1);
      toast.success(t("pol_confirmed_toast"));
    } finally {
      setChecking(false);
    }
  };

  const cards = [
    {
      to: "/vault",
      icon: Lock,
      count: (summary?.vault_count ?? 0) + (summary?.capsule_count ?? 0),
      k: "nav_vault",
    },
    {
      to: "/messages",
      icon: MessageSquareHeart,
      count: (summary?.messages_count ?? 0) + (summary?.stories_count ?? 0),
      k: "nav_messages",
    },
    { to: "/heirs", icon: Users, count: summary?.heirs_count ?? 0, k: "nav_heirs" },
  ];

  const pol = summary?.proof_of_life;
  const nextDate = pol?.next_checkin
    ? new Date(pol.next_checkin).toLocaleDateString()
    : "—";
  const plan = summary?.plan || user?.plan || "free";

  return (
    <div data-testid="dashboard-page">
      <Onboarding />
      <PaymentSuccessBanner />
      <EmailVerifyBanner />
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-label text-gold mb-3">{t("dash_welcome")}</div>
          <h1 className="font-display text-4xl md:text-6xl">
            {user?.name?.split(" ")[0] || "—"}.
          </h1>
        </div>
        <Link
          to="/plans"
          className="glass-card rounded-md px-3 py-2 sm:px-5 sm:py-3 hover:border-gold transition flex items-center gap-2 sm:gap-3"
          data-testid="dashboard-plan-pill"
        >
          <Sparkles className="w-4 h-4 text-gold shrink-0" />
          <div className="min-w-0">
            <div className="font-label text-faded text-[9px] sm:text-[10px] mb-0.5 sm:mb-1 truncate" style={{ letterSpacing: "0.12em" }}>{t("plan_current")}</div>
            <div className="text-gold-light text-xs sm:text-sm truncate">{PLAN_LABEL[plan]}</div>
          </div>
        </Link>
      </div>

      {/* PoL banner — the single most critical action, always visible at the top */}
      <PoLBanner
        pol={{
          state: pol?.state,            // canonical state from backend
          status: pol?.status,
          next_check_at: pol?.next_checkin,
        }}
        onCheckIn={handleCheckin}
        busy={checking}
      />

      <SetupProgress />
      <LegacyHealth key={refreshKey} />

      {/* Compact info card below — info only, no duplicate CTA */}
      <div className="glass-card rounded-md p-6 md:p-8 mb-10" data-testid="pol-info-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-5">
            <HeartPulse className="w-7 h-7 text-gold mt-1" strokeWidth={1.4} />
            <div>
              <div className="font-label text-gold mb-2">{t("dash_pol")}</div>
              <div className="font-display text-2xl text-gold-light mb-1" data-testid="pol-info-state">
                {pol?.state ? t(`pol_state_${pol.state}`) : "…"}
              </div>
              <div className="text-sm text-muted">
                {t("dash_pol_next")}: <span className="text-gold-light">{nextDate}</span>
              </div>
            </div>
          </div>
          <Link
            to="/proof-of-life"
            className="text-sm text-gold-light/80 hover:text-gold-light inline-flex items-center gap-1.5"
            data-testid="pol-view-details"
          >
            {t("pol_view_details")}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link key={c.to} to={c.to} data-testid={`card-${c.k}`}>
              <div className="glass-card rounded-md p-6 md:p-8 group hover:border-gold transition-all h-full">
                <div className="flex items-start justify-between mb-6">
                  <Icon className="w-6 h-6 md:w-7 md:h-7 text-gold" strokeWidth={1.2} />
                  <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 text-faded group-hover:text-gold transition-colors" />
                </div>
                <div className="font-display text-4xl md:text-5xl text-gold-light mb-2">{c.count}</div>
                <div className="font-label text-faded">{t(c.k)}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
